import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';
import { logAction } from '../lib/actionLogger';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

// ── Variable Expenses ────────────────────────────────────────────────────────

export function useExpenses() {
  return useQuery({
    queryKey: queryKeys.expenses.all,
    queryFn: async () => {
      const data = await api.get('/expenses');
      return data.map(mapFromDb);
    },
  });
}

export function useExpensesByJob(jobId) {
  return useQuery({
    queryKey: queryKeys.expenses.byJob(jobId),
    queryFn: async () => {
      const data = await api.get(`/expenses?job_id=${jobId}`);
      return data.map(mapFromDb);
    },
    enabled: !!jobId,
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newExpense) => {
      const data = await api.post('/expenses', mapToDb(newExpense));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      if (data.jobId) {
        qc.invalidateQueries({ queryKey: queryKeys.expenses.byJob(data.jobId) });
      }
      logAction({
        action: 'Create Expense',
        jobId: data.jobId,
        details: { amount: data.amount, category: data.category, vendor: data.vendor },
      });
    },
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (expense) => {
      const { id, ...updates } = expense;
      const data = await api.put(`/expenses/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      if (data.jobId) {
        qc.invalidateQueries({ queryKey: queryKeys.expenses.byJob(data.jobId) });
      }
      logAction({
        action: 'Update Expense',
        jobId: data.jobId,
        details: { amount: data.amount, category: data.category },
      });
    },
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (expenseId) => {
      await api.del(`/expenses/${expenseId}`);
    },
    onSuccess: (_data, expenseId) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      qc.invalidateQueries({ queryKey: ['expenses', 'job'] });
      logAction({
        action: 'Delete Expense',
        details: { expenseId },
      });
    },
  });
}

// ── Static Expenses (Recurring) ──────────────────────────────────────────────

export function useStaticExpenses() {
  return useQuery({
    queryKey: queryKeys.staticExpenses.all,
    queryFn: async () => {
      const data = await api.get('/static-expenses');
      return data.map(mapFromDb);
    },
  });
}

export function useCreateStaticExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newExpense) => {
      const data = await api.post('/static-expenses', mapToDb(newExpense));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.staticExpenses.all });
      logAction({
        action: 'Create Static Expense',
        details: { name: data.name, amount: data.amount, frequency: data.frequency },
      });
    },
  });
}

export function useUpdateStaticExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (expense) => {
      const { id, ...updates } = expense;
      const data = await api.put(`/static-expenses/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.staticExpenses.all });
      logAction({
        action: 'Update Static Expense',
        details: { name: data.name, amount: data.amount },
      });
    },
  });
}

export function useDeleteStaticExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (expenseId) => {
      await api.del(`/static-expenses/${expenseId}`);
    },
    onSuccess: (_data, expenseId) => {
      qc.invalidateQueries({ queryKey: queryKeys.staticExpenses.all });
      logAction({
        action: 'Delete Static Expense',
        details: { expenseId },
      });
    },
  });
}
